import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { MembersFilters } from '@/components/members/members-filters'
import { MembersTable } from '@/components/members/members-table'
import { Pagination } from '@/components/members/pagination'
import { InviteMemberModal } from '@/components/members/invite-member-modal'
import { Button } from '@/components/ui/button'
import { useMembersList } from '@/hooks/use-members'
import type { MemberListItem } from '@riverpark/types'

export interface MembersListConfig {
  basePath: string
  title?: string
  description?: string
  showInvite?: boolean
  hideHeader?: boolean
}

export function MembersListPage({
  basePath,
  title = 'Members',
  description = 'View and manage estate member accounts.',
  showInvite = false,
  hideHeader = false,
}: MembersListConfig) {
  const [inviteOpen, setInviteOpen] = useState(false)
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [status, setStatus] = useState<MemberListItem['status'] | 'all'>('all')
  const [role, setRole] = useState<MemberListItem['role'] | 'all'>('all')

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput)
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [searchInput])

  useEffect(() => {
    setPage(1)
  }, [status, role])

  const limit = 20
  const { data, isLoading, isFetching, error } = useMembersList({
    page,
    limit,
    search: debouncedSearch || undefined,
    status: status === 'all' ? undefined : status,
    role: role === 'all' ? undefined : role,
  })

  const members = data?.data ?? []
  const meta = data?.meta ?? { page: 1, limit, total: 0, totalPages: 1 }

  return (
    <PageShell>
      {!hideHeader && (
        <PageHeader
          title={title}
          label="Management"
          description={description}
          action={
            showInvite ? (
              <div className="flex flex-wrap gap-2">
                <Link
                  to="/admin/members/import"
                  className="inline-flex h-10 items-center justify-center rounded border border-border bg-transparent px-5 font-sans text-[13px] font-medium tracking-wide text-ink transition-colors hover:border-ink"
                >
                  Import members
                </Link>
                <Button type="button" onClick={() => setInviteOpen(true)}>
                  Invite member
                </Button>
              </div>
            ) : undefined
          }
        />
      )}

      {showInvite && <InviteMemberModal open={inviteOpen} onClose={() => setInviteOpen(false)} />}

      {error && (
        <p className="rounded border border-destructive/30 bg-status-overdue-bg px-4 py-3 font-sans text-sm text-destructive">
          {error instanceof Error ? error.message : 'Failed to load members'}
        </p>
      )}

      <MembersFilters
        search={searchInput}
        onSearchChange={setSearchInput}
        status={status}
        onStatusChange={setStatus}
        role={role}
        onRoleChange={setRole}
        total={meta.total}
      />

      <div className="space-y-0">
        <MembersTable
          members={members}
          basePath={basePath}
          isLoading={isLoading || (isFetching && members.length === 0)}
        />
        <Pagination
          page={meta.page}
          totalPages={meta.totalPages}
          total={meta.total}
          limit={meta.limit}
          onPageChange={setPage}
          className="rounded-b-card border border-t-0 border-border-2 bg-card"
        />
      </div>
    </PageShell>
  )
}
